import { useCallback, useRef, useState } from 'react'

import useEasytabsVisibility from 'Pods/Shared/Hooks/useEasytabsVisibility'
import useNotifications from 'Pods/Shared/Hooks/useNotifications'
import I18n from 'Pods/Shared/I18n'

import useCancelFee from './useCancelFee'
import useScheduledChange from './useScheduledChange'
import useSubscription from './useSubscription'
import useSubscriptionSettingsTracking from './useSubscriptionSettingsTracking'
import useSwitchPlanPreview from './useSwitchPlanPreview'
import { resolveEntryStep } from '../Components/CancelSubscriptionModal/useCancelModal'
import {
  PAUSE_ERROR_ALIASES, PAUSE_ERROR_KEYS, PLAN_TYPE, SUBSCRIPTION_STATUS, TIER, isRestructureLayout,
} from '../constants'

const USAGE_DISABLED_STATUSES = [
  SUBSCRIPTION_STATUS.CANCELED,
  SUBSCRIPTION_STATUS.PAUSED,
  SUBSCRIPTION_STATUS.REDEMPTION,
  SUBSCRIPTION_STATUS.PAST_DUE,
  SUBSCRIPTION_STATUS.SUSPENDED,
]

// The fee preview returns formatted amounts (e.g. "USD 124.97"); keep only the numeric value.
const parseAmount = (value) => {
  if (typeof value === 'number') return value

  return parseFloat(String(value).replace(/[^0-9.]/g, '')) || 0
}

const SCHEDULED_CHANGE_PENDING = 'scheduled_change_pending'

// The BE refuses a pause while a change is pending. An undo is not mirrored here: only the BE knows the
// pause dates, so the caller reloads instead.
const withPauseBlocked = (pauseOffer, scheduledChange) => {
  if (!pauseOffer || !scheduledChange || pauseOffer.blockedReason) return pauseOffer

  return { available: false, blockedReason: SCHEDULED_CHANGE_PENDING, startsAt: null, resumesAt: null }
}

// The scheduled change endpoints return only scheduledChange; mirror the serializer's planChanges gating.
// Any other BE reason (e.g. a cancellation kept during the renewal window) outranks the pending change.
const withScheduledChange = (subscription, scheduledChange) => {
  if (!subscription.planChanges) return { ...subscription, scheduledChange }

  const { blockedReason: currentReason } = subscription.planChanges
  const otherReason = currentReason === SCHEDULED_CHANGE_PENDING ? null : currentReason
  const blockedReason = otherReason || (scheduledChange ? SCHEDULED_CHANGE_PENDING : null)
  const targets = subscription.planChanges.targets.map((target) => ({
    ...target, available: !blockedReason && !target.blockedReason,
  }))

  return {
    ...subscription,
    scheduledChange,
    planChanges: { ...subscription.planChanges, blockedReason, targets },
    pauseOffer: withPauseBlocked(subscription.pauseOffer, scheduledChange),
  }
}

function useSubscriptionManagement({
  subscription = null,
  locale = 'en',
  subscriptionV16Enabled = false,
  subscriptionPauseEnabled = false,
  plansRestructureEnabled = false,
}) {
  const [subscriptionData, setSubscriptionData] = useState(subscription)
  const [isSwitchPlanModalOpen, setIsSwitchPlanModalOpen] = useState(false)
  const [isPlanChangeModalOpen, setIsPlanChangeModalOpen] = useState(false)
  const [isPauseConfirmationOpen, setIsPauseConfirmationOpen] = useState(false)
  const [cancelConfirmation, setCancelConfirmation] = useState(null)
  const [resumeRequested, setResumeRequested] = useState(false)
  const [resumeInProgress, setResumeInProgress] = useState(false)
  const resumeInFlightRef = useRef(false)
  const {
    feeData, isFeeLoading, feeError, fetchFee, resetFeeData,
  } = useCancelFee({ subscriptionId: subscriptionData?.id })
  const {
    previewData, isPreviewLoading, fetchPreview, resetPreview,
  } = useSwitchPlanPreview()
  const { showError } = useNotifications()

  // Plan-type fact, independent of the flag: monthly subscribers (incl. ones from the v1.6
  // window) must skip the early-cancellation fee fetch even when the flag is off.
  const isMonthlyPlan = subscriptionData?.planType === 'monthly'
  const isAiPlan = subscriptionData?.tier === TIER.AI_STUDIO

  // The v1.6 cancel flow (survey step, consequences screen, confirmation modal) ships behind
  // subscription_pause_enabled on top of the already-rolled-out subscription_v1_6, so the new
  // screens reach only the pause rollout. Mirrors cancel_flow_v16_enabled? in the API controller.
  // CGTrader AI always gets it: subscription_v1_6 also gates the multi-plan checkout and stays off.
  const isRestructure = isRestructureLayout(subscriptionData, plansRestructureEnabled)
  // Released rows cancel at period end with no fee, so the fee preview is never needed.
  const isFeeFreeFlow = isRestructure && Boolean(subscriptionData?.commitmentReleased)
  // A cancel during a live user pause ends the subscription right away (no access-until date).
  const cancelEndsNow = isFeeFreeFlow
    && subscriptionData?.status === SUBSCRIPTION_STATUS.PAUSED
    && Boolean(subscriptionData?.userPause)
  const isCancelFlowV16Enabled = (subscriptionV16Enabled && subscriptionPauseEnabled) || isAiPlan || isFeeFreeFlow
  // With a pause scheduled, nextBillingDate is the auto-resume day; the paid period ends when the pause would start.
  const accessUntilDate = isFeeFreeFlow && !cancelEndsNow
    ? subscriptionData?.userPauseScheduledAt || subscriptionData?.nextBillingDate || null
    : null

  const trackingData = {
    tier: subscriptionData?.tier ?? null,
    planType: subscriptionData?.planType,
    daysSinceSubStart: subscriptionData?.daysSinceSubStart,
    downloadsUsed: subscriptionData?.downloadsUsed,
    billingTerm: subscriptionData?.billingTerm,
  }

  const {
    trackSectionView,
    trackCancelClick,
    trackRenewClick,
    trackRenewSuccess,
    trackRenewFailure,
    trackReactivateClick,
    trackReactivateSuccess,
    trackReactivateFailure,
    trackRetryClick,
    trackPauseScheduled,
    trackPauseFailure,
    trackPauseResumeClick,
    trackPauseResumeSuccess,
    trackPauseResumeFailure,
  } = useSubscriptionSettingsTracking(trackingData)

  const isExpired = subscriptionData?.status === SUBSCRIPTION_STATUS.EXPIRED

  const translate = (key, options = {}) =>
    I18n.t(key, { ns: 'rails', lng: locale, ...options })

  const handleTabVisible = useCallback(() => {
    if (subscriptionData) {
      trackSectionView()
    }
  }, [subscriptionData, trackSectionView])

  useEasytabsVisibility('tab-settings-subscription', handleTabVisible)

  const pauseBlockedReason = subscriptionData?.pauseOffer?.blockedReason
  const handleScheduledChangeUpdate = useCallback((scheduledChange) => {
    if (!scheduledChange && pauseBlockedReason === SCHEDULED_CHANGE_PENDING) {
      window.location.reload()
      return
    }

    setSubscriptionData((prev) => withScheduledChange(prev, scheduledChange))
  }, [pauseBlockedReason])

  const {
    scheduleChange, isScheduling, keepPlan, isKeepingPlan,
  } = useScheduledChange({
    onChange: handleScheduledChangeUpdate,
    messages: {
      scheduleError: translate('subscription_management.switch_plan_modal.toast.error'),
      upgradeInProgressError: translate('subscription_management.switch_plan_modal.toast.upgrade_in_progress'),
      renewalInProgressError: translate('subscription_management.switch_plan_modal.toast.renewal_in_progress'),
      keepError: translate('subscription_management.scheduled_switch_banner.cancel_error'),
    },
  })

  const handleOpenPlanChange = useCallback(() => setIsPlanChangeModalOpen(true), [])
  const handleClosePlanChange = useCallback(() => setIsPlanChangeModalOpen(false), [])

  const handleRestoreSuccess = useCallback(
    ({ renewalScheduled = false } = {}) => {
      // The response carries no subscription, so a reload renders every renewed field from fresh props.
      const reload = renewalScheduled ? undefined : () => window.location.reload()

      if (!renewalScheduled) {
        setSubscriptionData((prev) => ({
          ...prev,
          canceledAt: null,
          renewsOn: prev.periodEndDate,
        }))
      }

      if (isExpired) {
        trackReactivateSuccess(subscriptionData?.id, reload)
      } else {
        trackRenewSuccess(subscriptionData?.id, reload)
      }
    },
    [isExpired, subscriptionData?.id, trackReactivateSuccess, trackRenewSuccess]
  )

  const handleRestoreError = useCallback(
    (errorMessage) => {
      if (isExpired) {
        trackReactivateFailure(subscriptionData?.id, errorMessage)
      } else {
        trackRenewFailure(subscriptionData?.id, errorMessage)
      }
    },
    [isExpired, subscriptionData?.id, trackReactivateFailure, trackRenewFailure]
  )

  const handleRedirectToPaymentForm = useCallback((redirectUrl) => {
    const fallbackUrl = '/profile/subscription/payment-form'
    window.location.assign(redirectUrl || fallbackUrl)
  }, [])

  const handleCancelPlanSwitchSuccess = useCallback(() => {
    setSubscriptionData((prev) => ({
      ...prev,
      scheduledPlanSwitchTo: null,
      scheduledPlanSwitchAt: null,
      // Solidgate sets canceled_at via webhook when the switch is committed.
      // Clear it optimistically on cancel since restore_subscription undoes that.
      canceledAt: null,
      renewsOn: prev.periodEndDate,
    }))
  }, [])

  const pauseErrorMessage = useCallback((errorCode) => {
    const code = PAUSE_ERROR_ALIASES[errorCode] || errorCode
    const key = PAUSE_ERROR_KEYS.includes(code) ? code : 'generic'
    return I18n.t(`subscription_management.pause.errors.${key}`, { ns: 'rails', lng: locale })
  }, [locale])

  const handlePauseError = useCallback((errorCode) => {
    trackPauseFailure(subscriptionData?.id, errorCode)
  }, [trackPauseFailure, subscriptionData?.id])

  const handleResumeError = useCallback((errorCode) => {
    trackPauseResumeFailure(subscriptionData?.id, errorCode)
  }, [trackPauseResumeFailure, subscriptionData?.id])

  // cancel_subscription answers an empty 200, so the confirmation reuses what the modal showed:
  // the settings date on the fee-free flow, the fee preview otherwise. Monthly plans skip the fee
  // fetch, so they get the no-fee/no-date confirmation variant.
  const handleCancelSuccess = useCallback(() => {
    if (isFeeFreeFlow) {
      setCancelConfirmation({
        accessUntil: accessUntilDate, accessUntilLabel: null, cancelFee: 0, endsNow: cancelEndsNow,
      })
      return
    }

    setCancelConfirmation({
      accessUntil: feeData?.access_until || null,
      // CGTrader AI skips the fee fetch, so the serialized period end is the only date source.
      accessUntilLabel: isAiPlan ? subscriptionData?.periodEndDate || null : null,
      cancelFee: parseAmount(feeData?.cancel_fee),
      endsNow: false,
    })
  }, [isFeeFreeFlow, accessUntilDate, cancelEndsNow, feeData, isAiPlan, subscriptionData?.periodEndDate])

  const {
    isCancelModalOpen,
    isLoading: isRestoreLoading,
    handleOpenCancelModal,
    handleCloseCancelModal,
    handleCancelSubscription,
    handleCaptureCancellationReason,
    handleRenewSubscription,
    handleReactivateSubscription,
    handleRetryPayment,
    retryState,
    handleCancelPlanSwitch,
    isCancelPlanSwitchLoading,
    handlePauseSubscription,
    isPauseLoading,
    handleResumeSubscription,
    isResumeLoading,
  } = useSubscription({
    subscriptionData,
    messages: {
      cancelError: translate('subscription_management.cancel_modal.error'),
      restoreSuccess: translate('subscription_management.restore.success'),
      restoreError: translate('subscription_management.restore.error'),
      retryError: translate('subscription_management.retry_payment.error.default'),
      cancelPlanSwitchSuccess: translate('subscription_management.scheduled_switch_banner.cancel_success'),
      cancelPlanSwitchError: translate('subscription_management.scheduled_switch_banner.cancel_error'),
      pauseError: pauseErrorMessage,
      resumeError: pauseErrorMessage,
    },
    onCancelSuccess: isCancelFlowV16Enabled ? handleCancelSuccess : null,
    onRestoreSuccess: handleRestoreSuccess,
    onRestoreError: handleRestoreError,
    onRedirectToPaymentForm: handleRedirectToPaymentForm,
    onCancelPlanSwitchSuccess: handleCancelPlanSwitchSuccess,
    onPauseError: handlePauseError,
    onResumeError: handleResumeError,
  })

  // Legacy eligibility mirrors the backend guard: rolled out to this user, active, on a yearly
  // (commitment) plan, no cancellation scheduled, no pause scheduled. Allowance exhaustion is
  // only known server-side and surfaces as a 422. Resuming an existing pause is deliberately
  // not gated, so a user who paused while enrolled keeps the control if the rollout flag is
  // turned off -- or if their plan is no longer pausable.
  const isLegacyPauseEligible =
    subscriptionPauseEnabled &&
    subscriptionData?.status === SUBSCRIPTION_STATUS.ACTIVE &&
    subscriptionData?.planType === PLAN_TYPE.YEARLY &&
    !isAiPlan &&
    !subscriptionData?.canceledAt &&
    !subscriptionData?.userPauseScheduledAt &&
    !subscriptionData?.scheduledPlanSwitchTo
  // Pause v2 eligibility comes from the BE; the kill switch still applies.
  const showPauseOffer = isRestructure
    ? subscriptionPauseEnabled && Boolean(subscriptionData?.pauseOffer?.available)
    : isLegacyPauseEligible

  // Keeps the pause-eligible and pause-ineligible populations apart in the funnel. Resolved by
  // the modal's own rule so the reported step is always the one the user lands on.
  const entryStep = resolveEntryStep({ showPauseOffer, hasReasonStep: isCancelFlowV16Enabled })

  const handleCancelClick = useCallback(async () => {
    trackCancelClick(subscriptionData?.id, entryStep)
    if (!isMonthlyPlan && !isAiPlan && !isFeeFreeFlow) {
      if (showPauseOffer) {
        // The modal opens on the pause offer, which needs no fee data - prefetch in the
        // background instead of delaying the offer; the cancel step keeps submit blocked
        // until the fee settles (isFeeLoading below).
        fetchFee()
      } else {
        await fetchFee()
      }
    }
    handleOpenCancelModal()
  }, [
    trackCancelClick,
    subscriptionData?.id,
    entryStep,
    fetchFee,
    handleOpenCancelModal,
    isMonthlyPlan,
    isAiPlan,
    isFeeFreeFlow,
    showPauseOffer,
  ])

  const handlePauseRequest = useCallback(async () => {
    const succeeded = await handlePauseSubscription()
    if (!succeeded) return

    trackPauseScheduled(subscriptionData?.id)
    resetFeeData()
    handleCloseCancelModal()
    setIsPauseConfirmationOpen(true)
  }, [
    handlePauseSubscription,
    trackPauseScheduled,
    subscriptionData?.id,
    resetFeeData,
    handleCloseCancelModal,
  ])

  // The pause is persisted server-side (user_pause_scheduled_at) before Solidgate confirms,
  // so a full reload renders the scheduled state from fresh props.
  const handlePauseConfirmationDone = useCallback(() => {
    setIsPauseConfirmationOpen(false)
    window.location.reload()
  }, [])

  // Reload renders the cancelled state from fresh props, matching the flag-off path.
  const handleCancelConfirmationDone = useCallback(() => {
    window.location.reload()
  }, [])

  const handleResumePauseClick = useCallback(async () => {
    // A duplicate click is not a funnel entry: no click hit, no request. Emitting one would
    // leave a click with no outcome, which reads in GA4 as a success lost to the reload.
    // The lock is a ref, not state: a second click dispatched before React re-renders still
    // reads the old state, and the whole gap this guards is the pre-render one.
    if (resumeInFlightRef.current) return

    // Not a duplicate but a broken control. Reported so the failure event carries the signal
    // instead of the page going silent.
    if (!subscriptionData?.id) {
      trackPauseResumeFailure(null, 'resume_unavailable')
      return
    }

    resumeInFlightRef.current = true
    trackPauseResumeClick(subscriptionData.id)

    const succeeded = await handleResumeSubscription()
    if (!succeeded) {
      resumeInFlightRef.current = false
      return
    }

    // isResumeLoading is already cleared here, so keep the control locked past the tracking
    // flush window -- otherwise a second click re-POSTs an already-deleted pause.
    setResumeRequested(true)

    if (subscriptionData?.status === SUBSCRIPTION_STATUS.PAUSED) {
      // A live pause resumes only when the Solidgate webhook lands; show progress
      // instead of reloading into an unchanged paused page.
      setResumeInProgress(true)
      trackPauseResumeSuccess(subscriptionData.id)
    } else {
      // A scheduled pause is deleted synchronously server-side; reload shows it gone.
      trackPauseResumeSuccess(subscriptionData.id, () => window.location.reload())
    }
  }, [
    trackPauseResumeClick,
    subscriptionData?.id,
    subscriptionData?.status,
    handleResumeSubscription,
    trackPauseResumeSuccess,
    trackPauseResumeFailure,
  ])

  const handleCloseCancel = useCallback(() => {
    resetFeeData()
    handleCloseCancelModal()
  }, [resetFeeData, handleCloseCancelModal])

  const handleRenewClick = useCallback(() => {
    trackRenewClick(subscriptionData?.id)
    handleRenewSubscription()
  }, [subscriptionData?.id, trackRenewClick, handleRenewSubscription])

  const handleReactivateClick = useCallback(() => {
    trackReactivateClick(subscriptionData?.id)
    handleReactivateSubscription()
  }, [subscriptionData?.id, trackReactivateClick, handleReactivateSubscription])

  const handleRetryClick = useCallback(() => {
    trackRetryClick(subscriptionData?.id)
    handleRetryPayment()
  }, [subscriptionData?.id, trackRetryClick, handleRetryPayment])

  const handleSwitchSuccess = useCallback((responseData) => {
    const scheduledTo = responseData?.scheduled_plan_switch_to ?? null
    setSubscriptionData((prev) => ({
      ...prev,
      scheduledPlanSwitchTo: scheduledTo,
      scheduledPlanSwitchAt: responseData?.scheduled_plan_switch_at ?? null,
      // Monthly→Annual takes effect immediately — reflect the new plan in the UI right away
      ...(scheduledTo === PLAN_TYPE.YEARLY && { planType: PLAN_TYPE.YEARLY, billingCycle: PLAN_TYPE.YEARLY }),
    }))
  }, [])

  const handleSwitchPlanClick = useCallback(async () => {
    if (isPreviewLoading || subscriptionData?.scheduledPlanSwitchTo) return
    const currentPlan = subscriptionData?.planType === PLAN_TYPE.MONTHLY
      ? PLAN_TYPE.MONTHLY
      : PLAN_TYPE.YEARLY
    const targetPlan = currentPlan === PLAN_TYPE.YEARLY ? PLAN_TYPE.MONTHLY : PLAN_TYPE.YEARLY
    const data = await fetchPreview({ to: targetPlan })
    if (data) {
      setIsSwitchPlanModalOpen(true)
    } else {
      showError(translate('subscription_management.switch_plan_modal.preview_toast_error'))
    }
  }, [
    isPreviewLoading,
    subscriptionData?.planType,
    subscriptionData?.scheduledPlanSwitchTo,
    fetchPreview,
    showError,
    translate,
  ])

  const handleCloseSwitchPlanModal = useCallback(() => {
    setIsSwitchPlanModalOpen(false)
    resetPreview()
  }, [resetPreview])

  const isUsageDisabled = USAGE_DISABLED_STATUSES.includes(subscriptionData?.status)

  // When Annual→Monthly switch is scheduled, Solidgate internally cancels the subscription
  // (force: false), which sets canceled_at via webhook. Override for display so the card
  // shows Active instead of PENDING_CANCEL while the user is still in the committed period.
  const subscriptionForDisplay = subscriptionData?.scheduledPlanSwitchTo === PLAN_TYPE.MONTHLY
    ? { ...subscriptionData, canceledAt: null, renewsOn: subscriptionData.periodEndDate }
    : subscriptionData

  return {
    translate,
    subscriptionData,
    subscriptionForDisplay,
    trackingData,
    isUsageDisabled,
    feeData,
    feeError,
    isFeeLoading,
    isCancelModalOpen,
    isRestoreLoading,
    retryState,
    handleCancelClick,
    handleCloseCancel,
    handleCancelSubscription,
    handleCaptureCancellationReason,
    handleRenewClick,
    handleReactivateClick,
    handleRetryClick,
    handleCancelPlanSwitch,
    isCancelPlanSwitchLoading,
    isCancelFlowV16Enabled,
    isFeeFreeFlow,
    cancelEndsNow,
    accessUntilDate,
    showPauseOffer,
    handlePauseRequest,
    isPauseLoading,
    isPauseConfirmationOpen,
    handlePauseConfirmationDone,
    cancelConfirmation,
    handleCancelConfirmationDone,
    handleResumePauseClick,
    isResumeLoading,
    resumeRequested,
    resumeInProgress,
    isSwitchPlanModalOpen,
    previewData,
    handleSwitchPlanClick,
    handleCloseSwitchPlanModal,
    handleSwitchSuccess,
    isPlanChangeModalOpen,
    handleOpenPlanChange,
    handleClosePlanChange,
    scheduleChange,
    isScheduling,
    keepPlan,
    isKeepingPlan,
  }
}

export default useSubscriptionManagement
